import { useState, useEffect } from "react";
import { 
  BookOpen, 
  Lightbulb, 
  TrendingUp, 
  AlertTriangle, 
  Rocket, 
  Bot, 
  BookMarked,
  ChevronDown,
  ChevronRight,
  Search,
  GraduationCap,
  Sparkles
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import { MarkdownText } from "@/components/MarkdownText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserGuide = () => {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    fetchGuide();
  }, []);

  const fetchGuide = async () => {
    try {
      const response = await axios.get(`${API}/guide`);
      setGuide(response.data);
    } catch (error) {
      console.error("Error fetching guide:", error);
      toast.error("Failed to load user guide");
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (iconName) => {
    const icons = {
      "money": <TrendingUp className="w-6 h-6" />,
      "basket": <BookOpen className="w-6 h-6" />,
      "warning": <AlertTriangle className="w-6 h-6" />,
      "rocket": <Rocket className="w-6 h-6" />,
      "robot": <Bot className="w-6 h-6" />,
      "dictionary": <BookMarked className="w-6 h-6" />
    };
    return icons[iconName] || <Lightbulb className="w-6 h-6" />;
  };

  const filteredSections = guide?.sections?.filter(section => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      section.title.toLowerCase().includes(term) ||
      section.content.toLowerCase().includes(term) ||
      section.examples?.some(ex => ex.toLowerCase().includes(term)) ||
      section.simple_explanation?.toLowerCase().includes(term)
    );
  });

  const filteredDictionary = guide?.dictionary?.filter(term => {
    if (!searchTerm) return true;
    return (
      term.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.simple.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.example.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <BookOpen className="w-12 h-12 mx-auto animate-pulse text-primary" />
          <p className="text-muted-foreground">Loading your guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto" data-testid="user-guide-page">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full">
          <GraduationCap className="w-10 h-10 text-primary" />
        </div>
        <h1 className="text-3xl font-bold">{guide?.title || "Beginner's Guide to Investing"}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {guide?.subtitle || "Learn investing basics in simple, easy-to-understand language!"}
        </p>
        <Badge variant="outline" className="text-sm">
          <Sparkles className="w-3 h-3 mr-1" />
          Written in Primary School English
        </Badge>
      </div>

      {/* Search */}
      <div className="relative max-w-md mx-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search topics, terms, or concepts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="guide-search"
        />
      </div>

      {/* Quick Jump Navigation */}
      <Card className="bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Quick Jump</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {guide?.sections?.map((section, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: 'smooth' });
                  setExpandedSection(`section-${index}`);
                }}
              >
                {section.emoji} {section.title.split(':')[0]}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => document.getElementById('dictionary')?.scrollIntoView({ behavior: 'smooth' })}
            >
              📚 Dictionary
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Sections */}
      <div className="space-y-4">
        {filteredSections?.map((section, index) => (
          <Card key={index} id={`section-${index}`} className="overflow-hidden">
            <CardHeader 
              className="cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => setExpandedSection(expandedSection === `section-${index}` ? null : `section-${index}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    {getSectionIcon(section.icon)}
                  </div>
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="text-2xl">{section.emoji}</span>
                      {section.title}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {section.simple_explanation?.slice(0, 80)}...
                    </CardDescription>
                  </div>
                </div>
                {expandedSection === `section-${index}` ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
            
            {expandedSection === `section-${index}` && (
              <CardContent className="border-t pt-4 space-y-4">
                {/* Simple Explanation */}
                <div className="bg-primary/5 p-4 rounded-lg border-l-4 border-primary">
                  <h4 className="font-semibold text-sm mb-2 text-primary">Simple Explanation:</h4>
                  <p className="text-base">{section.simple_explanation}</p>
                </div>

                {/* Main Content */}
                <MarkdownText>{section.content}</MarkdownText>

                {/* Examples */}
                {section.examples?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      Real-Life Examples:
                    </h4>
                    <ul className="space-y-2">
                      {section.examples.map((example, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm bg-amber-500/5 p-3 rounded-lg">
                          <span className="text-amber-500 shrink-0">💡</span>
                          <span>{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key Points */}
                {section.key_points?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">Key Points to Remember:</h4>
                    <ul className="grid gap-2">
                      {section.key_points.map((point, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded">
                          <span className="text-success">✓</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tips */}
                {section.tips?.length > 0 && (
                  <div className="bg-success/10 p-4 rounded-lg">
                    <h4 className="font-semibold text-sm mb-2 text-success">Top Tips:</h4>
                    <ul className="space-y-1">
                      {section.tips.map((tip, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <span>🎯</span> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Dictionary Section */}
      <Card id="dictionary" className="mt-8">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <BookMarked className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-xl">📚 Word Dictionary</CardTitle>
              <CardDescription>
                Investment words explained in simple terms
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {filteredDictionary?.map((term, index) => (
              <AccordionItem key={index} value={`term-${index}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">{term.word}</Badge>
                    <span className="text-sm text-muted-foreground">- {term.simple.slice(0, 40)}...</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-2">
                  <div>
                    <p className="font-medium text-sm text-primary">What it means:</p>
                    <p>{term.simple}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="font-medium text-sm text-amber-500">Example:</p>
                    <p className="text-sm">{term.example}</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Footer Help */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="py-6 text-center">
          <h3 className="text-lg font-semibold mb-2">Need More Help?</h3>
          <p className="text-muted-foreground mb-4">
            Try our Portfolio Wizard to create your first investment portfolio with AI guidance!
          </p>
          <Button onClick={() => window.location.href = '/wizard'}>
            <Sparkles className="w-4 h-4 mr-2" />
            Start Portfolio Wizard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserGuide;
